import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { CheckCircle } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import { useGetFeedbackQuery, useResolveFeedbackMutation } from "../feedbackApi";

const STATUS_OPTIONS = [
  { value: "Open", label: "Open" },
  { value: "Resolved", label: "Resolved" },
  { value: "", label: "All" },
];

// Simple triage inbox for in-app feedback (handoff §14, testing-phase only)
// - a capture list with an inline "Mark resolved" action, not a full
// ticketing UI (no detail page, no threads). Admin.ManageFeedback-gated.
const FeedbackInbox = () => {
  const [status, setStatus] = useState("Open");
  const { data: items = [], isLoading } = useGetFeedbackQuery({
    status: status || undefined,
  });
  const [resolveFeedback, { isLoading: isResolving }] = useResolveFeedbackMutation();
  const [resolving, setResolving] = useState(null);
  const [remark, setRemark] = useState("");

  const openResolve = (item) => {
    setResolving(item);
    setRemark("");
  };
  const closeResolve = () => setResolving(null);

  const handleResolve = async () => {
    try {
      await resolveFeedback({ feedbackId: resolving.feedbackId, resolvedRemark: remark || null }).unwrap();
      toast.success("Marked resolved");
      closeResolve();
    } catch (error) {
      toast.error(error?.message || "Could not resolve");
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "createdDate",
      width: 140,
      render: (v) => dayjs(v).format("DD-MMM-YYYY HH:mm"),
    },
    {
      title: "Reporter",
      dataIndex: "reporterName",
      render: (v, record) => v || record.userName || "-",
    },
    { title: "Branch", dataIndex: "branchCode", render: (v) => v || "-" },
    { title: "Page", dataIndex: "pageContext", render: (v) => v || "-" },
    { title: "Message", dataIndex: "message", ellipsis: { showTitle: true }, width: 320 },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => (
        <span className={`badge ${value === "Open" ? "text-bg-warning" : "badge-linesuccess"}`}>
          {value}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) =>
        record.status === "Open" ? (
          <button
            type="button"
            className="btn btn-link p-2"
            title="Mark resolved"
            onClick={() => openResolve(record)}
          >
            <CheckCircle size={16} />
          </button>
        ) : (
          <span className="text-muted small">{record.resolvedByName}</span>
        ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Feedback</h4>
              <h6>In-app issue reports from the testing phase</h6>
            </div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="form-sort">
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={items} rowKey="feedbackId" loading={isLoading} />
            </div>
          </div>
        </div>
      </div>

      <Modal show={Boolean(resolving)} onHide={closeResolve} centered>
        <Modal.Header closeButton>
          <Modal.Title>Mark Resolved</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">{resolving?.message}</p>
          <label className="form-label">Resolution Remark (optional)</label>
          <textarea className="form-control" rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeResolve}>Cancel</Button>
          <Button variant="primary" onClick={handleResolve} disabled={isResolving}>
            {isResolving ? "Saving..." : "Mark Resolved"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FeedbackInbox;
